/* eslint-disable max-lines, max-lines-per-function, max-statements */
import {
  FormEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type ZoneId = 'pacific' | 'queensland' | 'philippines';
type ViewMode = 'day' | 'fourDays' | 'week' | 'month';
type CalendarKind = 'event' | 'task';

type Zone = {
  id: ZoneId;
  name: string;
  label: string;
  city: string;
  iana: string;
  color: string;
};

type CalendarEvent = {
  id: string;
  title: string;
  startUtc: string;
  endUtc: string;
  zoneId: ZoneId;
  color: string;
  description: string;
  kind?: CalendarKind;
  completed?: boolean;
  work?: string;
};

type EventDraft = {
  id?: string;
  title: string;
  date: string;
  endDate: string;
  start: string;
  end: string;
  zoneId: ZoneId;
  color: string;
  description: string;
  kind: CalendarKind;
  completed: boolean;
  work: string;
};

type CloudRecord = { id: string; event: CalendarEvent | null; updatedAt: number };
type JournalEntry = { id: string; journal_date: string; content: string; created_at: number };
type DailyQuote = { quote_date: string; quote: string; updated_at: number };

type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const zones: Zone[] = [
  { id: 'pacific', name: 'Temi', label: 'Pacific time', city: 'Vancouver / Los Angeles', iana: 'America/Los_Angeles', color: '#4f7cff' },
  { id: 'queensland', name: 'Flow', label: 'Queensland time', city: 'Brisbane', iana: 'Australia/Brisbane', color: '#78a982' },
  { id: 'philippines', name: 'Home', label: 'Philippine time', city: 'Manila', iana: 'Asia/Manila', color: '#c88786' },
];

const eventColors = ['#4f7cff', '#78a982', '#c88786', '#d7a06f', '#8d789b'];
const colorNames = ['Blue', 'Sage', 'Nude pink', 'Sand', 'Mauve'];
const shiftStartMinute = 21 * 60;
const shiftDurationMinutes = 20 * 60;
const shiftHours = Array.from({ length: 20 }, (_, index) => index);

function getZone(id: ZoneId): Zone {
  return zones.find((zone) => zone.id === id) || zones[0];
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

export function snapTimeInput(value: string): string {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return value;
  const total = Math.min(23 * 60 + 59, Number(match[1]) * 60 + Number(match[2]));
  const snapped = Math.round(total / 5) * 5;
  const bounded = Math.min(23 * 60 + 55, snapped);
  return pad(Math.floor(bounded / 60)) + ':' + pad(bounded % 60);
}

function getDateParts(date: Date, timeZone: string): DateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date).reduce<Record<string, number>>((result, part) => {
    if (part.type !== 'literal') result[part.type] = Number(part.value);
    return result;
  }, {});
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
    second: parts.second,
  };
}

function offsetMinutes(date: Date, timeZone: string): number {
  const parts = getDateParts(date, timeZone);
  const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return Math.round((wallAsUtc - date.getTime()) / 60000);
}

function wallTimeToUtc(value: string, timeZone: string): Date {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return new Date(value);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const first = new Date(wallAsUtc);
  const firstResult = new Date(wallAsUtc - offsetMinutes(first, timeZone) * 60000);
  return new Date(wallAsUtc - offsetMinutes(firstResult, timeZone) * 60000);
}

function dateKey(date: Date, timeZone: string): string {
  const parts = getDateParts(date, timeZone);
  return parts.year + '-' + pad(parts.month) + '-' + pad(parts.day);
}

function inputTime(date: Date, timeZone: string): string {
  const parts = getDateParts(date, timeZone);
  return pad(parts.hour) + ':' + pad(parts.minute);
}

function addDays(value: string, days: number): string {
  const date = new Date(value + 'T12:00:00Z');
  date.setUTCDate(date.getUTCDate() + days);
  return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
}

function startOfWeekMonday(value: string): string {
  const date = new Date(value + 'T12:00:00Z');
  const day = date.getUTCDay();
  return addDays(value, day === 0 ? -6 : 1 - day);
}

function monthTitle(value: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(value.slice(0, 7) + '-01T12:00:00Z'));
}

function dateTitle(value: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' })
    .format(new Date(value + 'T12:00:00Z'));
}

function timeTitle(date: Date, timeZone: string, hour12: boolean): string {
  const parts = getDateParts(date, timeZone);
  const minute = parts.minute === 0 ? '' : ':' + pad(parts.minute);
  if (!hour12) return pad(parts.hour) + minute;
  const hour = parts.hour % 12 || 12;
  const period = parts.hour >= 12 ? 'PM' : 'AM';
  return hour + minute + ' ' + period;
}

function shiftStart(date: string): Date {
  return wallTimeToUtc(date + 'T21:00', getZone('philippines').iana);
}

function shiftEnd(date: string): Date {
  return wallTimeToUtc(addDays(date, 1) + 'T17:00', getZone('philippines').iana);
}

function plannerDateForNow(date: Date): string {
  return dateKey(date, getZone('philippines').iana);
}

function activeShiftDate(selectedDate: string, now: Date): string {
  if (selectedDate !== plannerDateForNow(now)) return selectedDate;
  const previousDate = addDays(selectedDate, -1);
  return now >= shiftStart(previousDate) && now < shiftEnd(previousDate) ? previousDate : selectedDate;
}

export function currentVisibleShiftDate(days: string[], now: Date): string | null {
  return days.find((day) => now >= shiftStart(day) && now < shiftEnd(day)) || null;
}

export function fourDayStartDate(selectedDate: string, now: Date): string {
  return addDays(activeShiftDate(selectedDate, now), -1);
}

function shiftHour(date: string, index: number): Date {
  const totalMinute = shiftStartMinute + index * 60;
  const wallDate = addDays(date, Math.floor(totalMinute / 1440));
  return wallTimeToUtc(wallDate + 'T' + pad(Math.floor(totalMinute % 1440 / 60)) + ':00', getZone('philippines').iana);
}

function eventOverlapsShift(event: CalendarEvent, date: string): boolean {
  return new Date(event.startUtc) < shiftEnd(date) && new Date(event.endUtc) > shiftStart(date);
}

export function relocateEvent(event: CalendarEvent, startUtc: string): CalendarEvent {
  const duration = new Date(event.endUtc).getTime() - new Date(event.startUtc).getTime();
  return { ...event, startUtc, endUtc: new Date(new Date(startUtc).getTime() + duration).toISOString() };
}

function conversionDate(date: Date, zone: Zone, hour12: boolean): string {
  return dateTitle(dateKey(date, zone.iana), { weekday: 'short', month: 'short', day: 'numeric' }) + ', ' + timeTitle(date, zone.iana, hour12);
}

function dateShiftMarker(event: CalendarEvent, displayZone: Zone): string {
  const instant = new Date(event.startUtc);
  const sourceDate = dateKey(instant, getZone(event.zoneId).iana);
  const displayedDate = dateKey(instant, displayZone.iana);
  if (sourceDate === displayedDate) return '';
  const dayNumber = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return Math.round(Date.UTC(year, month - 1, day) / 86400000);
  };
  const difference = dayNumber(displayedDate) - dayNumber(sourceDate);
  return ' · ' + (difference > 0 ? '+' : '') + difference + 'd';
}

function zoneOffsetTitle(instant: Date, zone: Zone): string {
  const minutes = offsetMinutes(instant, zone.iana);
  const sign = minutes >= 0 ? '+' : '−';
  const absolute = Math.abs(minutes);
  return 'UTC' + sign + Math.floor(absolute / 60) + (absolute % 60 ? ':' + pad(absolute % 60) : '');
}

function zoneAbbreviation(instant: Date, zone: Zone): string {
  if (zone.id === 'queensland') return 'AEST';
  if (zone.id === 'philippines') return 'PHT';
  return new Intl.DateTimeFormat('en-US', { timeZone: zone.iana, timeZoneName: 'short' }).formatToParts(instant).find((part) => part.type === 'timeZoneName')?.value || 'PT';
}

function draftInstants(draft: EventDraft): { start: Date; end: Date } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date) || !/^\d{4}-\d{2}-\d{2}$/.test(draft.endDate) || !/^\d{2}:\d{2}$/.test(draft.start) || !/^\d{2}:\d{2}$/.test(draft.end)) return null;
  const zone = getZone(draft.zoneId);
  const start = wallTimeToUtc(draft.date + 'T' + draft.start, zone.iana);
  const end = wallTimeToUtc(draft.endDate + 'T' + draft.end, zone.iana);
  return Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) ? { start, end } : null;
}

function daysInMonth(value: string): string[] {
  const month = new Date(value.slice(0, 7) + '-01T12:00:00Z');
  const firstDay = month.getUTCDay();
  const daysInCurrentMonth = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)).getUTCDate();
  const start = new Date(month);
  start.setUTCDate(1 - firstDay);
  const visibleDayCount = firstDay + daysInCurrentMonth <= 35 ? 35 : 42;
  return Array.from({ length: visibleDayCount }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return day.getUTCFullYear() + '-' + pad(day.getUTCMonth() + 1) + '-' + pad(day.getUTCDate());
  });
}

function makeId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function validImportedEvent(value: unknown): value is CalendarEvent {
  if (!value || typeof value !== 'object') return false;
  const event = value as Partial<CalendarEvent>;
  return typeof event.id === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(event.id) && typeof event.title === 'string' && !!event.title.trim() && event.title.length <= 300
    && typeof event.startUtc === 'string' && Number.isFinite(Date.parse(event.startUtc))
    && typeof event.endUtc === 'string' && Number.isFinite(Date.parse(event.endUtc))
    && Date.parse(event.endUtc) > Date.parse(event.startUtc)
    && !!event.zoneId && zones.some((zone) => zone.id === event.zoneId)
    && typeof event.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(event.color)
    && typeof event.description === 'string' && event.description.length <= 10000
    && (event.kind === undefined || event.kind === 'event' || event.kind === 'task')
    && (event.completed === undefined || typeof event.completed === 'boolean')
    && (event.work === undefined || (typeof event.work === 'string' && event.work.length <= 80));
}

async function cloudRequest(key: string, operations?: { id: string; event: CalendarEvent | null }[]): Promise<{ records?: CloudRecord[] }> {
  const response = await fetch('/api/events', {
    method: operations ? 'POST' : 'GET',
    headers: { ...(operations ? { 'Content-Type': 'application/json' } : {}) },
    ...(operations ? { body: JSON.stringify({ operations }) } : {}),
    cache: 'no-store',
  });
  if (response.status === 401) throw new Error('Invalid sync key. Check the key and try again.');
  if (!response.ok) throw new Error('Cloud sync is unavailable (' + response.status + '). No changes were saved.');
  return response.json() as Promise<{ records?: CloudRecord[] }>;
}

async function journalRequest(key: string, options: { date?: string; content?: string; startDate?: string; endDate?: string } = {}): Promise<{ entries?: JournalEntry[] }> {
  const queryParams = new URLSearchParams();
  if (options.date) queryParams.set('date', options.date);
  if ('startDate' in options && options.startDate) queryParams.set('startDate', options.startDate);
  if ('endDate' in options && options.endDate) queryParams.set('endDate', options.endDate);
  const query = queryParams.toString() ? '?' + queryParams.toString() : '';
  const response = await fetch('/api/journal' + query, {
    method: options.content ? 'POST' : 'GET',
    headers: { ...(options.content ? { 'Content-Type': 'application/json' } : {}) },
    ...(options.content ? { body: JSON.stringify({ date: options.date, content: options.content }) } : {}),
    cache: 'no-store',
  });
  if (response.status === 401) throw new Error('Invalid sync key. Check the key and try again.');
  if (!response.ok) throw new Error('Journal unavailable (' + response.status + ').');
  return response.json() as Promise<{ entries?: JournalEntry[] }>;
}

async function cleanupJournalRequest(text: string): Promise<{ cleaned: string }> {
  const response = await fetch('/api/journal-cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('Cleanup unavailable (' + response.status + ').');
  return response.json() as Promise<{ cleaned: string }>;
}

async function quoteRequest(key: string, date: string, quote?: string): Promise<{ quote?: DailyQuote | null }> {
  const response = await fetch('/api/quotes?date=' + encodeURIComponent(date), {
    method: quote === undefined ? 'GET' : 'POST',
    headers: quote === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(quote === undefined ? {} : { body: JSON.stringify({ date, quote }) }),
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('Quote unavailable (' + response.status + ').');
  return response.json() as Promise<{ quote?: DailyQuote | null }>;
}

function MiniCalendar(props: { selectedDate: string; onSelect: (date: string) => void; onMoveMonth: (amount: number) => void }) {
  const days = daysInMonth(props.selectedDate);
  const monthPrefix = props.selectedDate.slice(0, 7);
  return (
    <section className="mini-calendar">
      <div className="section-heading">
        <h2>{monthTitle(props.selectedDate)}</h2>
        <div className="icon-actions">
          <button className="icon-button small" onClick={() => props.onMoveMonth(-1)} aria-label="Previous month">‹</button>
          <button className="icon-button small" onClick={() => props.onMoveMonth(1)} aria-label="Next month">›</button>
        </div>
      </div>
      <div className="weekday-row">{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={day + index}>{day}</span>)}</div>
      <div className="mini-grid">
        {days.map((day) => (
          <button key={day} className={'mini-day ' + (day === props.selectedDate ? 'selected ' : '') + (day.startsWith(monthPrefix) ? '' : 'muted')} onClick={() => props.onSelect(day)}>
            {Number(day.slice(-2))}
          </button>
        ))}
      </div>
    </section>
  );
}

function CreateMenu(props: { onCreate: (kind: CalendarKind) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const choose = (kind: CalendarKind) => { setOpen(false); props.onCreate(kind); };
  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);
  return <div ref={menuRef} className={'create-menu ' + (props.compact ? 'compact' : '')}><button className={props.compact ? 'floating-create' : 'create-button'} onClick={() => setOpen((value) => !value)} aria-haspopup="menu" aria-expanded={open}><span className="create-plus">＋</span><strong>Create</strong><span className="create-caret">⌄</span></button>{open && <div className="create-menu-popover" role="menu"><button type="button" onClick={() => choose('event')} role="menuitem"><span className="menu-dot event-dot" />Event</button><button type="button" onClick={() => choose('task')} role="menuitem"><span className="menu-dot task-dot" />Task</button></div>}</div>;
}

function Sidebar(props: {
  selectedDate: string;
  collapsed: boolean;
  onSelect: (date: string) => void;
  onMoveMonth: (amount: number) => void;
  onExpand: () => void;
  quote: string;
  tasks: CalendarEvent[];
  onToggleTask: (task: CalendarEvent) => void;
}) {
  if (props.collapsed) {
    return <button className="sidebar-expand" onClick={props.onExpand} aria-label="Show sidebar">›</button>;
  }
  return (
    <aside className="sidebar">
      <MiniCalendar selectedDate={props.selectedDate} onSelect={props.onSelect} onMoveMonth={props.onMoveMonth} />
      <div className="daily-quote"><span className="eyebrow">Daily motivation</span><p>{props.quote || 'No quote for this day.'}</p></div>
      <div className="todo-list"><div className="todo-heading"><span className="eyebrow">To do</span><span>{props.tasks.length}</span></div>{props.tasks.length === 0 ? <p className="todo-empty">No outstanding tasks.</p> : props.tasks.slice(0, 8).map((task) => <label className="todo-item" key={task.id}><input type="checkbox" onChange={() => props.onToggleTask(task)} /><span><strong>{task.title}</strong>{task.description && <small className="todo-description">{task.description}</small>}</span></label>)}{props.tasks.length > 8 && <small className="todo-more">+{props.tasks.length - 8} more</small>}</div>
    </aside>
  );
}

function ZoneHeadings(props: { date: string; zoneOrder: ZoneId[]; onReorder: (from: ZoneId, to: ZoneId) => void }) {
  const [draggedZone, setDraggedZone] = useState<ZoneId | null>(null);
  const instant = shiftStart(props.date);
  const now = new Date();
  return (
    <div className="time-zone-headings" aria-label="Drag to arrange your time zones">
      {props.zoneOrder.map((zoneId) => {
        const zone = getZone(zoneId);
        const hour = getDateParts(now, zone.iana).hour;
        const [clock, period] = timeTitle(now, zone.iana, true).split(' ');
        return <span key={zone.id} draggable title={zone.label + ' · ' + zoneAbbreviation(instant, zone) + ' · ' + zoneOffsetTitle(instant, zone)} onDragStart={() => setDraggedZone(zone.id)} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (draggedZone) props.onReorder(draggedZone, zone.id); setDraggedZone(null); }} onDragEnd={() => setDraggedZone(null)}><i className="zone-heading-dot" style={{ backgroundColor: zone.color }} /><strong>{zone.name}</strong><small className="zone-live-time" aria-label={clock + ' ' + period}><time>{clock}</time><b>{period} {hour >= 6 && hour < 18 ? '☼' : '◐'}</b></small><small>{zoneAbbreviation(instant, zone)}</small></span>;
      })}
    </div>
  );
}

type MovePreview = { event: CalendarEvent; date: string; startMinute: number };

export function moveStartMinute(pointerMinute: number, grabOffsetMinute: number): number {
  return snapTimelineStart(pointerMinute - grabOffsetMinute);
}

function EventBlock(props: { event: CalendarEvent; zone: Zone; selectedDate: string; hour12: boolean; onOpen: () => void; onResize?: (event: CalendarEvent, endUtc: string) => void; onMove?: (event: CalendarEvent, startUtc: string) => void; onMovePreview?: (preview: MovePreview | null) => void }) {
  const [resizeEnd, setResizeEnd] = useState<number | null>(null);
  const [moving, setMoving] = useState(false);
  const resized = useRef(false);
  const moved = useRef(false);
  const moveDrag = useRef<{ pointerId: number; x: number; y: number; lastX: number; lastY: number; grabOffset: number; ready: boolean; scrolling: boolean; timer?: number } | null>(null);
  const shiftStartTime = shiftStart(props.selectedDate).getTime();
  const shiftLength = shiftEnd(props.selectedDate).getTime() - shiftStartTime;
  const start = Math.max(0, new Date(props.event.startUtc).getTime() - shiftStartTime);
  const storedEnd = Math.min(shiftLength, new Date(props.event.endUtc).getTime() - shiftStartTime);
  const end = resizeEnd === null ? storedEnd : resizeEnd - shiftStartTime;
  const top = start / shiftLength * 100;
  const shortEvent = end - start <= 15 * 60000;
  const height = Math.min(100 - top, shortEvent ? Math.max(.25, (end - start) / shiftLength * 100) : Math.max(2.2, (end - start) / shiftLength * 100));
  const startDate = new Date(props.event.startUtc);
  const endDate = new Date(shiftStartTime + end);
  const resizeAt = (clientY: number, grid: DOMRect): number => {
    const rawMinute = (clientY - grid.top) / grid.height * shiftDurationMinutes;
    const snappedMinute = Math.round(rawMinute / 5) * 5;
    const minimum = Math.ceil((start / 60000 + 5) / 5) * 5;
    const minute = Math.max(minimum, Math.min(shiftDurationMinutes, snappedMinute));
    return shiftStartTime + minute * 60000;
  };
  const onResizeStart = (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resized.current = false;
    setResizeEnd(shiftStartTime + storedEnd);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onResizeMove = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    const grid = event.currentTarget.closest('.selectable-grid')?.getBoundingClientRect();
    if (grid) {
      setResizeEnd(resizeAt(event.clientY, grid));
      resized.current = true;
    }
  };
  const onResizeEnd = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    const grid = event.currentTarget.closest('.selectable-grid')?.getBoundingClientRect();
    const finalEnd = grid ? resizeAt(event.clientY, grid) : resizeEnd;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setResizeEnd(null);
    if (finalEnd && finalEnd !== shiftStartTime + storedEnd) {
      resized.current = true;
      props.onResize?.(props.event, new Date(finalEnd).toISOString());
    }
  };
  const moveTarget = (clientX: number, clientY: number): MovePreview | null => {
    const view = document.querySelector('.timesheet, .multi-day-view');
    const grid = document.elementsFromPoint(clientX, clientY).find((element) => element instanceof HTMLElement && element.classList.contains('selectable-grid') && view?.contains(element)) as HTMLElement | undefined;
    const date = grid?.dataset.shiftDate;
    if (!grid || !date || !moveDrag.current) return null;
    const rect = grid.getBoundingClientRect();
    const pointerMinute = (clientY - rect.top) / rect.height * shiftDurationMinutes;
    return { event: props.event, date, startMinute: moveStartMinute(pointerMinute, moveDrag.current.grabOffset) };
  };
  const onMoveStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    if (event.pointerType !== 'touch') event.preventDefault();
    const grid = event.currentTarget.closest('.selectable-grid')?.getBoundingClientRect();
    if (!grid) return;
    moved.current = false;
    moveDrag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, lastY: event.clientY, grabOffset: Math.max(0, (event.clientY - event.currentTarget.getBoundingClientRect().top) / grid.height * shiftDurationMinutes), ready: event.pointerType !== 'touch', scrolling: false };
    event.currentTarget.setPointerCapture(event.pointerId);
    if (event.pointerType === 'touch') {
      moveDrag.current.timer = window.setTimeout(() => {
        if (moveDrag.current?.pointerId === event.pointerId) moveDrag.current.ready = true;
      }, 350);
    }
  };
  const onMoveUpdate = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = moveDrag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - current.x, event.clientY - current.y);
    if (event.pointerType === 'touch' && !current.ready && distance > 6) {
      current.scrolling = true;
      if (current.timer) window.clearTimeout(current.timer);
    }
    if (current.scrolling) {
      const scroller = event.currentTarget.closest('.multi-day-view, .timesheet')?.querySelector('.multi-day-scroll, .timesheet-scroll');
      if (scroller) {
        scroller.scrollTop += current.lastY - event.clientY;
        scroller.scrollLeft += current.lastX - event.clientX;
      }
      current.lastX = event.clientX;
      current.lastY = event.clientY;
      return;
    }
    if (distance <= 4 || !current.ready) return;
    event.preventDefault();
    event.stopPropagation();
    moved.current = true;
    setMoving(true);
    props.onMovePreview?.(moveTarget(event.clientX, event.clientY));
  };
  const onMoveEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = moveDrag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (current.timer) window.clearTimeout(current.timer);
    const target = moved.current && !current.scrolling ? moveTarget(event.clientX, event.clientY) : null;
    if (current.scrolling) moved.current = true;
    moveDrag.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setMoving(false);
    props.onMovePreview?.(null);
    if (target) props.onMove?.(props.event, new Date(shiftStart(target.date).getTime() + target.startMinute * 60000).toISOString());
  };
  const onMoveCancel = () => {
    if (moveDrag.current?.timer) window.clearTimeout(moveDrag.current.timer);
    moveDrag.current = null;
    setMoving(false);
    props.onMovePreview?.(null);
  };
  return (
    <button className={'event-block ' + (moving ? 'is-moving ' : '') + (shortEvent ? 'is-short ' : '') + (props.event.kind === 'task' ? 'is-task ' : '') + (props.event.completed ? 'is-complete' : '')} title={props.event.title + ' · ' + timeTitle(startDate, props.zone.iana, props.hour12) + ' – ' + timeTitle(endDate, props.zone.iana, props.hour12)} style={{ top: top + '%', height: height + '%', backgroundColor: props.event.color + '18', borderLeftColor: props.event.color }} onPointerDown={onMoveStart} onPointerMove={onMoveUpdate} onPointerUp={onMoveEnd} onPointerCancel={onMoveCancel} onClick={(click) => { click.stopPropagation(); if (resized.current || moved.current) { resized.current = false; moved.current = false; return; } props.onOpen(); }}>
      <strong>{props.event.kind === 'task' ? (props.event.completed ? '☑ ' : '☐ ') : ''}{props.event.work ? props.event.work + ' · ' : ''}{props.event.title}</strong>
      <span>{props.zone.name} · {timeTitle(startDate, props.zone.iana, props.hour12)} – {timeTitle(endDate, props.zone.iana, props.hour12)}{dateShiftMarker(props.event, props.zone)}</span>
      {props.event.description && !shortEvent && <span className="event-description">{props.event.description}</span>}
      <span className="resize-handle" role="separator" aria-label="Adjust event end time" onPointerDown={onResizeStart} onPointerMove={onResizeMove} onPointerUp={onResizeEnd} onPointerCancel={onResizeEnd} />
    </button>
  );
}

function TimeRail(props: { date: string; zoneOrder: ZoneId[]; hour12: boolean; hoverMinute?: number | null; hoverDate?: string }) {
  const hoverInstant = props.hoverMinute === undefined || props.hoverMinute === null ? null : new Date(shiftStart(props.hoverDate || props.date).getTime() + props.hoverMinute * 60000);
  return <div className="time-rail" aria-label="Aligned Temi, Flow, and Home clocks">
    {shiftHours.map((hour) => {
      const instant = shiftHour(props.date, hour);
      return <div className="time-row" key={hour}>{props.zoneOrder.map((zoneId) => {
        const zone = getZone(zoneId);
        return <span key={zone.id} title={zone.label + ' · ' + conversionDate(instant, zone, props.hour12)}><b>{timeTitle(instant, zone.iana, props.hour12)}</b></span>;
      })}</div>;
    })}
    {hoverInstant && <div className="hover-time-row" style={{ top: (props.hoverMinute || 0) / shiftDurationMinutes * 100 + '%' }}>{props.zoneOrder.map((zoneId) => { const zone = getZone(zoneId); return <span key={zone.id}>{timeTitle(hoverInstant, zone.iana, props.hour12)}</span>; })}</div>}
  </div>;
}

type TimeSelection = { start: number; end: number };
type DragState = { pointerId: number; start: number; clientX: number; clientY: number; lastX: number; lastY: number; moved: boolean; scrolling: boolean; ready: boolean; timer?: number };

export function snapTimelineStart(minute: number): number {
  return Math.max(0, Math.min(shiftDurationMinutes - 5, Math.round(minute / 5) * 5));
}

function selectionAt(start: number, clientY: number, rect: DOMRect): TimeSelection {
  const minute = Math.max(0, Math.min(shiftDurationMinutes, (clientY - rect.top) / rect.height * shiftDurationMinutes));
  if (minute < start) return { start: snapTimelineStart(minute), end: start + 5 };
  return { start, end: Math.max(start + 5, Math.min(shiftDurationMinutes, Math.ceil(minute / 5) * 5)) };
}

function TimeSelectionGrid(props: { date: string; zone: Zone; now: Date; hour12: boolean; events?: CalendarEvent[]; onCreate: (start: number, end: number) => void; onOpen?: (event: CalendarEvent) => void; onResize?: (event: CalendarEvent, endUtc: string) => void; onMove?: (event: CalendarEvent, startUtc: string) => void; movePreview?: MovePreview | null; onMovePreview?: (preview: MovePreview | null) => void; hoverMinute?: number | null; onHover?: (minute: number | null) => void; multiDay?: boolean }) {
  const [selection, setSelection] = useState<TimeSelection | null>(null);
  const drag = useRef<DragState | null>(null);
  const startTime = shiftStart(props.date).getTime();
  const endTime = shiftEnd(props.date).getTime();
  const nowIsVisible = props.now.getTime() >= startTime && props.now.getTime() < endTime;
  const hoverAt = (clientY: number, rect: DOMRect) => {
    const minute = Math.max(0, Math.min(shiftDurationMinutes, (clientY - rect.top) / rect.height * shiftDurationMinutes));
    props.onHover?.(Math.round(minute / 5) * 5);
  };
  const clearDrag = () => {
    if (drag.current?.timer) window.clearTimeout(drag.current.timer);
    drag.current = null;
    setSelection(null);
  };
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || drag.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const raw = Math.max(0, Math.min(shiftDurationMinutes - 1, (event.clientY - rect.top) / rect.height * shiftDurationMinutes));
    const start = snapTimelineStart(raw);
    const isTouch = event.pointerType === 'touch';
    drag.current = { pointerId: event.pointerId, start, clientX: event.clientX, clientY: event.clientY, lastX: event.clientX, lastY: event.clientY, moved: false, scrolling: false, ready: !isTouch };
    event.currentTarget.setPointerCapture(event.pointerId);
    if (isTouch) {
      drag.current.timer = window.setTimeout(() => {
        if (drag.current?.pointerId === event.pointerId && !drag.current.scrolling) {
          drag.current.ready = true;
          setSelection({ start, end: Math.min(shiftDurationMinutes, start + 60) });
        }
      }, 350);
    } else {
      event.preventDefault();
      setSelection({ start, end: Math.min(shiftDurationMinutes, start + 60) });
    }
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) hoverAt(event.clientY, event.currentTarget.getBoundingClientRect());
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - current.clientX, event.clientY - current.clientY);
    if (event.pointerType === 'touch' && !current.ready && distance > 6) current.scrolling = true;
    if (current.scrolling) {
      const scroller = event.currentTarget.closest(props.multiDay ? '.multi-day-view' : '.timesheet-scroll');
      if (scroller) {
        scroller.scrollTop += current.lastY - event.clientY;
        scroller.scrollLeft += current.lastX - event.clientX;
      }
    } else if (current.ready && distance > 4) {
      current.moved = true;
      setSelection(selectionAt(current.start, event.clientY, event.currentTarget.getBoundingClientRect()));
    }
    current.lastX = event.clientX;
    current.lastY = event.clientY;
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const range = current.moved ? selectionAt(current.start, event.clientY, event.currentTarget.getBoundingClientRect()) : { start: current.start, end: Math.min(shiftDurationMinutes, current.start + 60) };
    const shouldCreate = !current.scrolling;
    clearDrag();
    if (shouldCreate) props.onCreate(range.start, range.end);
    props.onHover?.(null);
  };
  const preview = props.movePreview?.date === props.date ? props.movePreview : null;
  const previewDuration = preview ? (new Date(preview.event.endUtc).getTime() - new Date(preview.event.startUtc).getTime()) / 60000 : 0;
  return <div className={props.multiDay ? 'multi-day-column selectable-grid' : 'time-grid selectable-grid'} data-shift-date={props.date} aria-label={'Drag to select a time range on ' + props.date + ' in ' + props.zone.label} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerEnter={(event) => hoverAt(event.clientY, event.currentTarget.getBoundingClientRect())} onPointerLeave={() => { if (!drag.current) props.onHover?.(null); }} onPointerUp={onPointerUp} onPointerCancel={clearDrag}>
    {shiftHours.map((hour) => <div className="hour-line" key={hour} style={{ top: (hour / shiftHours.length * 100) + '%' }} />)}
    {shiftHours.map((hour) => <div className="half-hour-line" key={'half-' + hour} style={{ top: ((hour + .5) / shiftHours.length * 100) + '%' }} />)}
    {nowIsVisible && <div className="now-line" style={{ top: ((props.now.getTime() - startTime) / (endTime - startTime) * 100) + '%' }}><span /></div>}
    {props.onHover && props.hoverMinute !== undefined && props.hoverMinute !== null && <div className="hover-line" style={{ top: props.hoverMinute / shiftDurationMinutes * 100 + '%' }} />}
    {props.events?.map((event) => <EventBlock key={event.id} event={event} zone={props.zone} selectedDate={props.date} hour12={props.hour12} onOpen={() => props.onOpen?.(event)} onResize={props.onResize} onMove={props.onMove} onMovePreview={props.onMovePreview} />)}
    {preview && <div className="event-move-preview" style={{ top: preview.startMinute / shiftDurationMinutes * 100 + '%', height: Math.max(5, Math.min(previewDuration, shiftDurationMinutes - preview.startMinute)) / shiftDurationMinutes * 100 + '%', backgroundColor: preview.event.color + '30', borderLeftColor: preview.event.color }}><strong>{preview.event.title}</strong><span>{timeTitle(new Date(shiftStart(preview.date).getTime() + preview.startMinute * 60000), props.zone.iana, props.hour12)}</span></div>}
    {selection && <div className="time-selection" style={{ top: selection.start / shiftDurationMinutes * 100 + '%', height: (selection.end - selection.start) / shiftDurationMinutes * 100 + '%' }}><strong>{timeTitle(new Date(startTime + selection.start * 60000), props.zone.iana, props.hour12)} – {timeTitle(new Date(startTime + selection.end * 60000), props.zone.iana, props.hour12)}</strong><span>{props.zone.name} · New event or task</span></div>}
  </div>;
}

function TimeSheet(props: {
  zoneOrder: ZoneId[];
  selectedDate: string;
  events: CalendarEvent[];
  now: Date;
  hour12: boolean;
  focusNow: number;
  onReorder: (from: ZoneId, to: ZoneId) => void;
  onCreate: (zone: Zone, date: string, start: number, end: number) => void;
  onOpen: (event: CalendarEvent) => void;
  onResize: (event: CalendarEvent, endUtc: string) => void;
  onMove: (event: CalendarEvent, startUtc: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoverMinute, setHoverMinute] = useState<number | null>(null);
  const [movePreview, setMovePreview] = useState<MovePreview | null>(null);
  const anchorZone = getZone(props.zoneOrder[props.zoneOrder.length - 1]);
  const timelineDate = activeShiftDate(props.selectedDate, props.now);
  const showCurrentOvernightShift = timelineDate !== props.selectedDate;
  const homeDate = props.selectedDate === plannerDateForNow(props.now) ? plannerDateForNow(props.now) : props.selectedDate;
  const visibleEvents = props.events.filter((event) => event.kind !== 'task' && eventOverlapsShift(event, timelineDate));
  useEffect(() => {
    if (!props.focusNow) return;
    const frame = window.requestAnimationFrame(() => {
      const scroller = scrollRef.current;
      const nowLine = scroller?.querySelector<HTMLElement>('.now-line');
      if (scroller && nowLine) scroller.scrollTop = Math.max(0, nowLine.offsetTop - scroller.clientHeight / 2);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [props.focusNow, timelineDate, props.now]);
  return (
    <section className="timesheet">
      <header className="timesheet-header">
        <ZoneHeadings date={timelineDate} zoneOrder={props.zoneOrder} onReorder={props.onReorder} />
        <div className="timesheet-date"><span>HOME · {dateTitle(homeDate, { weekday: 'short' }).toUpperCase()}</span><strong>{Number(homeDate.slice(-2))}</strong></div>
        <span className="shift-caption">{showCurrentOvernightShift ? '9 PM previous day – 5 PM today' : '9 PM – 5 PM next day'}</span>
      </header>
      <div className="timesheet-scroll" ref={scrollRef}>
        <div className="timesheet-body">
          <TimeRail date={timelineDate} zoneOrder={props.zoneOrder} hour12={props.hour12} hoverMinute={hoverMinute} />
          <TimeSelectionGrid date={timelineDate} zone={anchorZone} now={props.now} hour12={props.hour12} events={visibleEvents} hoverMinute={hoverMinute} onHover={setHoverMinute} onCreate={(start, end) => props.onCreate(anchorZone, timelineDate, start, end)} onOpen={props.onOpen} onResize={props.onResize} onMove={props.onMove} movePreview={movePreview} onMovePreview={setMovePreview} />
        </div>
      </div>
    </section>
  );
}

function MultiDayView(props: {
  dayCount: 4 | 7;
  zoneOrder: ZoneId[];
  selectedDate: string;
  events: CalendarEvent[];
  now: Date;
  hour12: boolean;
  focusNow: number;
  onReorder: (from: ZoneId, to: ZoneId) => void;
  onCreate: (zone: Zone, date: string, start: number, end: number) => void;
  onOpen: (event: CalendarEvent) => void;
  onResize: (event: CalendarEvent, endUtc: string) => void;
  onMove: (event: CalendarEvent, startUtc: string) => void;
}) {
  const anchorZone = getZone(props.zoneOrder[props.zoneOrder.length - 1]);
  const calendarStart = props.dayCount === 7 ? startOfWeekMonday(props.selectedDate) : fourDayStartDate(props.selectedDate, props.now);
  const days = Array.from({ length: props.dayCount }, (_, index) => addDays(calendarStart, index));
  const activeDate = currentVisibleShiftDate(days, props.now)
    || (props.dayCount === 4 ? activeShiftDate(props.selectedDate, props.now) : props.selectedDate);
  const firstShift = shiftStart(calendarStart);
  const [hoveredTime, setHoveredTime] = useState<{ date: string; minute: number } | null>(null);
  const [movePreview, setMovePreview] = useState<MovePreview | null>(null);
  const hoverDay = (date: string, minute: number | null) => {
    setHoveredTime((current) => minute === null ? null : current?.date === date && current.minute === minute ? current : { date, minute });
  };
  const weekRef = useRef<HTMLElement>(null);
  const scrolledRange = useRef<string | null>(null);
  useEffect(() => {
    if (props.dayCount === 4 && weekRef.current) weekRef.current.scrollLeft = 0;
  }, [calendarStart, props.dayCount]);
  useEffect(() => {
    const rangeKey = props.dayCount + ':' + calendarStart;
    if (!weekRef.current || scrolledRange.current === rangeKey) return;
    const earliest = days.flatMap((day) => props.events
      .filter((event) => event.kind !== 'task' && eventOverlapsShift(event, day))
      .map((event) => Math.max(0, (new Date(event.startUtc).getTime() - shiftStart(day).getTime()) / 60000)))
      .reduce((minute, eventMinute) => Math.min(minute, eventMinute), Infinity);
    if (!Number.isFinite(earliest)) {
      weekRef.current.scrollTop = 0;
      return;
    }
    const columnHeight = weekRef.current.querySelector('.multi-day-column')?.getBoundingClientRect().height || 3600;
    weekRef.current.scrollTop = Math.max(0, earliest / shiftDurationMinutes * columnHeight - 120);
    scrolledRange.current = rangeKey;
  }, [calendarStart, props.dayCount, props.events]);
  useEffect(() => {
    if (!props.focusNow || !weekRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const scroller = weekRef.current;
      if (!scroller) return;
      const nowLine = scroller.querySelector<HTMLElement>('.now-line');
      if (nowLine) scroller.scrollTop = Math.max(0, nowLine.offsetTop - scroller.clientHeight / 2);
      if (props.dayCount === 4) {
        scroller.scrollLeft = 0;
        return;
      }
      const selectedDay = scroller.querySelector<HTMLElement>('.multi-day-day-head.selected');
      if (selectedDay) {
        const left = selectedDay.getBoundingClientRect().left - scroller.getBoundingClientRect().left + scroller.scrollLeft;
        scroller.scrollLeft = Math.max(0, left - 200);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [props.focusNow, props.dayCount]);
  return (
    <section ref={weekRef} className={'multi-day-view ' + (props.dayCount === 7 ? 'is-week' : 'is-four-day')}>
      <header className="multi-day-header">
        <div className="multi-day-rail-head"><ZoneHeadings date={calendarStart} zoneOrder={props.zoneOrder} onReorder={props.onReorder} /><small>9 PM – 5 PM next day · Home</small></div>
        {days.map((day) => {
          const instant = shiftStart(day);
          const nextDay = addDays(day, 1);
          const changedZones = props.zoneOrder.map(getZone).filter((zone) => zoneOffsetTitle(instant, zone) !== zoneOffsetTitle(firstShift, zone));
          return <div className={'multi-day-day-head ' + (day === activeDate ? 'selected' : '')} key={day} title={'Home ' + dateTitle(day, { month: 'short', day: 'numeric' }) + ' – ' + dateTitle(nextDay, { month: 'short', day: 'numeric' }) + ' · 9 PM – 5 PM next day · ' + props.zoneOrder.map((zoneId) => { const zone = getZone(zoneId); return zone.name + ' ' + zoneOffsetTitle(instant, zone); }).join(' · ')}><strong className="multi-day-date-range">{Number(day.slice(-2))}–{Number(nextDay.slice(-2))}</strong>{changedZones.length > 0 && <small className="day-offset">{changedZones.map((zone) => zone.name + ' ' + zoneOffsetTitle(instant, zone)).join(' · ')}</small>}</div>;
        })}
      </header>
      <div className="multi-day-scroll">
        <div className="multi-day-body">
          <TimeRail date={calendarStart} zoneOrder={props.zoneOrder} hour12={props.hour12} hoverMinute={hoveredTime?.minute} hoverDate={hoveredTime?.date} />
          <div className="multi-day-grid" style={{ gridTemplateColumns: 'repeat(' + props.dayCount + ', minmax(var(--day-column-min), 1fr))' }}>
            {days.map((day) => <TimeSelectionGrid key={day} date={day} zone={anchorZone} now={props.now} hour12={props.hour12} events={props.events.filter((event) => event.kind !== 'task' && eventOverlapsShift(event, day))} hoverMinute={hoveredTime?.date === day ? hoveredTime.minute : null} onHover={(minute) => hoverDay(day, minute)} multiDay onCreate={(start, end) => props.onCreate(anchorZone, day, start, end)} onOpen={props.onOpen} onResize={props.onResize} onMove={props.onMove} movePreview={movePreview} onMovePreview={setMovePreview} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

function MonthView(props: { selectedDate: string; primaryZone: Zone; events: CalendarEvent[]; hour12: boolean; onCreate: (date: string) => void; onOpen: (event: CalendarEvent) => void }) {
  const days = daysInMonth(props.selectedDate);
  const monthPrefix = props.selectedDate.slice(0, 7);
  return (
    <section className="month-view">
      <div className="month-grid">
        {days.map((day, index) => {
          const dayEvents = props.events.filter((event) => event.kind !== 'task' && dateKey(new Date(event.startUtc), props.primaryZone.iana) === day);
          return (
            <div className={'month-cell ' + (day.startsWith(monthPrefix) ? '' : 'outside ') + (day === props.selectedDate ? 'selected' : '')} key={day} onClick={() => props.onCreate(day)}>
              <header className={index >= 7 ? 'only-date' : ''}>{index < 7 && <span>{dateTitle(day, { weekday: 'short' }).toUpperCase()}</span>}<strong>{Number(day.slice(-2))}</strong></header>
              <div className="month-events">
                {dayEvents.slice(0, 4).map((event) => <button key={event.id} className={'month-event ' + (event.completed ? 'is-complete' : '')} style={{ borderLeftColor: event.color, backgroundColor: event.color + '15' }} title={(event.work ? event.work + ' · ' : '') + event.title + ' · ' + conversionDate(new Date(event.startUtc), props.primaryZone, props.hour12)} onClick={(click) => { click.stopPropagation(); props.onOpen(event); }}><span>{props.primaryZone.name} {timeTitle(new Date(event.startUtc), props.primaryZone.iana, props.hour12)}{dateShiftMarker(event, props.primaryZone)}</span> {event.kind === 'task' ? (event.completed ? '☑ ' : '☐ ') : ''}{event.work ? event.work + ' · ' : ''}{event.title}</button>)}
                {dayEvents.length > 4 && <span className="month-more">+{dayEvents.length - 4} more</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function QuickEventDialog(props: { draft: EventDraft; hour12: boolean; onChange: (draft: EventDraft) => void; onClose: () => void; onSave: () => void }) {
  const instants = draftInstants(props.draft);
  const startInstant = instants?.start || new Date();
  const endInstant = instants?.end || new Date(startInstant.getTime() + 60 * 60000);
  const home = getZone('philippines');
  const length = Math.max(5, Math.round((endInstant.getTime() - startInstant.getTime()) / 60000));
  const setInstants = (start: Date, end: Date, zoneId = props.draft.zoneId) => {
    const zone = getZone(zoneId);
    props.onChange({ ...props.draft, zoneId, color: zone.color, date: dateKey(start, zone.iana), start: inputTime(start, zone.iana), endDate: dateKey(end, zone.iana), end: inputTime(end, zone.iana) });
  };
  const updateStart = (value: string) => {
    const date = dateKey(startInstant, home.iana);
    const nextStart = wallTimeToUtc(date + 'T' + value, home.iana);
    setInstants(nextStart, new Date(nextStart.getTime() + length * 60000));
  };
  const updateDate = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    const nextStart = wallTimeToUtc(value + 'T' + inputTime(startInstant, home.iana), home.iana);
    setInstants(nextStart, new Date(nextStart.getTime() + length * 60000));
  };
  const updateLength = (value: string) => setInstants(startInstant, new Date(startInstant.getTime() + Number(value) * 60000));
  const updateZone = (zoneId: ZoneId) => setInstants(startInstant, endInstant, zoneId);
  const submit = (event: FormEvent) => { event.preventDefault(); props.onSave(); };
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <form className="quick-event-dialog" onSubmit={submit}>
        <label className="quick-title"><span className="sr-only">Event title</span><input autoFocus required maxLength={300} value={props.draft.title} onChange={(event) => props.onChange({ ...props.draft, title: event.target.value })} placeholder="New event" /></label>
        <div className="quick-zone-buttons" role="group" aria-label="Calendar zone">{zones.map((zone) => <button type="button" key={zone.id} className={props.draft.zoneId === zone.id ? 'selected ' + zone.id : zone.id} onClick={() => updateZone(zone.id)}><span />{zone.name}</button>)}</div>
        <label className="field quick-date"><span>Date</span><input type="date" value={dateKey(startInstant, home.iana)} onChange={(event) => updateDate(event.target.value)} /></label>
        <div className="quick-fields"><label className="field"><span>Starts (Home time)</span><input type="time" step={300} value={inputTime(startInstant, home.iana)} onChange={(event) => { if (event.target.value) updateStart(event.target.value); }} onBlur={(event) => { if (event.target.value) updateStart(snapTimeInput(event.target.value)); }} /></label><label className="field"><span>Length</span><select value={length} onChange={(event) => updateLength(event.target.value)}>{[5, 10, 15, 30, 45, 60, 90, 120, 180].map((minutes) => <option value={minutes} key={minutes}>{minutes < 60 ? minutes + ' minutes' : minutes % 60 === 0 ? minutes / 60 + ' hour' + (minutes === 60 ? '' : 's') : Math.floor(minutes / 60) + '.5 hours'}</option>)}</select></label></div>
        {instants && <section className="quick-conversions" aria-label="Converted event times">{zones.map((zone) => <div key={zone.id}><span><i className={zone.id} />{zone.name}</span><strong>{conversionDate(instants.start, zone, props.hour12)}</strong></div>)}</section>}
        <label className="field quick-description"><span>Description <em>optional</em></span><textarea rows={3} maxLength={10000} value={props.draft.description} onChange={(event) => props.onChange({ ...props.draft, description: event.target.value })} placeholder="Add notes or details" /></label>
        <div className="quick-footer"><button type="button" className="secondary-button" onClick={props.onClose}>Cancel</button><button type="submit" className="primary-button">Add</button></div>
      </form>
    </div>
  );
}

function QuickTaskDialog(props: { draft: EventDraft; onChange: (draft: EventDraft) => void; onClose: () => void; onSave: () => void; onDelete: () => void }) {
  const submit = (event: FormEvent) => { event.preventDefault(); props.onSave(); };
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <form className="quick-event-dialog quick-task-dialog" onSubmit={submit}>
        <div className="quick-task-eyebrow">{props.draft.id ? 'Edit task' : 'New task'}</div>
        <label className="quick-title"><span className="sr-only">Task title</span><input autoFocus required maxLength={300} value={props.draft.title} onChange={(event) => props.onChange({ ...props.draft, title: event.target.value })} placeholder="What needs to get done?" /></label>
        <label className="field quick-description"><span>Description <em>optional</em></span><textarea rows={6} maxLength={10000} value={props.draft.description} onChange={(event) => props.onChange({ ...props.draft, description: event.target.value })} placeholder="Add a little context for this task" /></label>
        <label className="task-complete"><input type="checkbox" checked={props.draft.completed} onChange={(event) => props.onChange({ ...props.draft, completed: event.target.checked })} /> Mark task complete</label>
        <div className="color-field"><span>Color</span><div className="color-options">{eventColors.map((color, index) => <button type="button" key={color} aria-label={colorNames[index] + (props.draft.color === color ? ', selected' : '')} aria-pressed={props.draft.color === color} className={'color-choice ' + (props.draft.color === color ? 'selected' : '')} style={{ backgroundColor: color }} onClick={() => props.onChange({ ...props.draft, color })} />)}</div></div>
        <div className="quick-footer">{props.draft.id && <button type="button" className="delete-button task-delete" onClick={props.onDelete}>Delete</button>}<span className="footer-spacer" /><button type="button" className="secondary-button" onClick={props.onClose}>Cancel</button><button type="submit" className="primary-button">{props.draft.id ? 'Save' : 'Add task'}</button></div>
      </form>
    </div>
  );
}

function EventDialog(props: { draft: EventDraft; hour12: boolean; onChange: (draft: EventDraft) => void; onClose: () => void; onSave: () => void; onDelete: () => void }) {
  const update = (field: keyof EventDraft, value: string) => props.onChange({ ...props.draft, [field]: value });
  const snapTime = (field: 'start' | 'end', value: string) => {
    if (value) update(field, snapTimeInput(value));
  };
  const submit = (event: FormEvent) => { event.preventDefault(); props.onSave(); };
  const instants = draftInstants(props.draft);
  const invalidRange = !!instants && instants.end <= instants.start;
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <form className="event-dialog" onSubmit={submit}>
        <div className="dialog-header"><div><span className="eyebrow">{props.draft.id ? 'Edit ' : 'New '}{props.draft.kind}</span><h2>{props.draft.id ? 'Update your schedule' : 'Add to your calendar'}</h2></div><button type="button" className="icon-button" onClick={props.onClose} aria-label="Close">×</button></div>
        <div className="kind-switch" role="group" aria-label="Create as"><button type="button" className={props.draft.kind === 'event' ? 'selected' : ''} aria-pressed={props.draft.kind === 'event'} onClick={() => props.onChange({ ...props.draft, kind: 'event', completed: false })}>Event</button><button type="button" className={props.draft.kind === 'task' ? 'selected' : ''} aria-pressed={props.draft.kind === 'task'} onClick={() => props.onChange({ ...props.draft, kind: 'task' })}>Task</button></div>
        <label className="field full"><span>Title</span><input autoFocus required maxLength={300} value={props.draft.title} onChange={(event) => update('title', event.target.value)} placeholder="e.g. Team sync" /></label>
        {props.draft.kind === 'event' && <>
          <p className="time-context">Times use <strong>{getZone(props.draft.zoneId).name} ({getZone(props.draft.zoneId).label})</strong> — the clock closest to the timesheet.</p>
          <div className="form-grid">
            <label className="field"><span>Start date</span><input type="date" required value={props.draft.date} onChange={(event) => update('date', event.target.value)} /></label>
            <label className="field"><span>Work / project <em>optional</em></span><input list="work-suggestions" maxLength={80} value={props.draft.work} onChange={(event) => update('work', event.target.value)} placeholder="e.g. Temi or Flow" /><datalist id="work-suggestions"><option value="Temi" /><option value="Flow" /><option value="Personal" /></datalist></label>
            <label className="field"><span>Starts</span><input type="time" step={300} required value={props.draft.start} onChange={(event) => update('start', event.target.value)} onBlur={(event) => snapTime('start', event.target.value)} /></label>
            <label className="field"><span>Ends</span><input type="time" step={300} required value={props.draft.end} onChange={(event) => update('end', event.target.value)} onBlur={(event) => snapTime('end', event.target.value)} /></label>
            <label className="field"><span>End date</span><input type="date" required value={props.draft.endDate} onChange={(event) => update('endDate', event.target.value)} /></label>
          </div>
          {invalidRange && <p className="form-error" role="alert">End must be after start. For an overnight event, select the next date.</p>}
          {instants && !invalidRange && <section className="conversion-preview" aria-label="Converted event times"><div className="conversion-heading"><strong>Across your clocks</strong><span>Dates update for overnight meetings</span></div><div className="conversion-grid"><span>Zone</span><span>Starts</span><span>Ends</span>{zones.map((zone) => <div className="conversion-row" key={zone.id}><strong>{zone.name}<small>{zone.label}</small></strong><span>{conversionDate(instants.start, zone, props.hour12)}</span><span>{conversionDate(instants.end, zone, props.hour12)}</span></div>)}</div></section>}
        </>}
        <label className="field full"><span>Description <em>optional</em></span><textarea rows={3} maxLength={10000} value={props.draft.description} onChange={(event) => update('description', event.target.value)} placeholder="Add notes, a location, or a handoff detail" /></label>
        {props.draft.kind === 'task' && <label className="task-complete"><input type="checkbox" checked={props.draft.completed} onChange={(event) => props.onChange({ ...props.draft, completed: event.target.checked })} /> Mark task complete</label>}
        <div className="color-field"><span>Color</span><div className="color-options">{eventColors.map((color, index) => <button type="button" key={color} aria-label={colorNames[index] + (props.draft.color === color ? ', selected' : '')} aria-pressed={props.draft.color === color} className={'color-choice ' + (props.draft.color === color ? 'selected' : '')} style={{ backgroundColor: color }} onClick={() => props.onChange({ ...props.draft, color })} />)}</div></div>
        <div className="dialog-footer">{props.draft.id && <button type="button" className="delete-button" onClick={props.onDelete}>⌫ Delete</button>}<span className="footer-spacer" /><button type="button" className="secondary-button" onClick={props.onClose}>Cancel</button><button type="submit" className="primary-button" disabled={invalidRange || !instants}>{props.draft.id ? 'Save changes' : 'Create ' + props.draft.kind}</button></div>
      </form>
    </div>
  );
}

function JournalView(props: { date: string; entries: JournalEntry[]; status: string; exportStatus: string; start: string; end: string; onStart: (value: string) => void; onEnd: (value: string) => void; onClear: () => void; onWrite: (date: string) => void; onExport: () => void }) {
  const entries = [...props.entries].reverse();
  const days = Array.from(new Set(entries.map((entry) => entry.journal_date)));
  const filterLabel = props.start && props.end && props.start === props.end ? dateTitle(props.start, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : props.start || props.end ? 'Selected dates' : 'All entries';
  return <section className="journal-view" aria-label="Journal entries">
    <div className="journal-view-heading"><div><h2>Journal</h2><p>Pick a day or a date range below.</p></div><button type="button" className="primary-button" onClick={() => props.onWrite(props.date)}>Write for {dateTitle(props.date, { month: 'short', day: 'numeric' })}</button></div>
    <div className="journal-view-toolbar"><div className="journal-view-filter"><strong>{filterLabel}</strong><button type="button" className="text-button" onClick={props.onClear} disabled={!props.start && !props.end}>Show all</button></div><div className="journal-export-fields"><label className="field"><span>From</span><input type="date" value={props.start} onChange={(event) => props.onStart(event.target.value)} /></label><label className="field"><span>To</span><input type="date" value={props.end} onChange={(event) => props.onEnd(event.target.value)} /></label><button type="button" className="secondary-button" onClick={props.onExport}>Export CSV</button></div>{props.exportStatus && <span className="journal-export-status" role="status">{props.exportStatus}</span>}</div>
    <div className="journal-view-list" aria-live="polite">{props.status === 'Loading…' ? <p className="empty-state">Loading entries…</p> : props.status.startsWith('Could not') || props.status.includes('unavailable') || props.status === 'Choose a valid date range' ? <p className="empty-state">{props.status}</p> : entries.length === 0 ? <p className="empty-state">No entries for these dates.</p> : days.map((day) => <section className="journal-view-day" key={day}><div className="journal-view-day-heading"><h3>{dateTitle(day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h3><button type="button" className="text-button" onClick={() => props.onWrite(day)}>Write</button></div>{entries.filter((entry) => entry.journal_date === day).map((entry) => <article className="journal-view-entry" key={entry.id}><time dateTime={new Date(entry.created_at).toISOString()}>{new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Manila' }).format(new Date(entry.created_at))} Home</time><p>{entry.content}</p></article>)}</section>)}</div>
  </section>;
}

function JournalPanel(props: { date: string; status: string; text: string; quote: string; cleanupStatus: string; onText: (value: string) => void; onQuote: (value: string) => void; onSaveQuote: () => void; onCleanup: () => void; onAdd: () => void; onClose: () => void }) {
  const busy = props.status === 'Saving…' || props.cleanupStatus === 'Cleaning…';
  const close = () => { if (!busy) props.onClose(); };
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="journal-panel">
        <div className="dialog-header"><div><span className="eyebrow">Journal</span><h2>{dateTitle(props.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h2></div><button className="icon-button" onClick={close} disabled={busy} aria-label="Close journal">×</button></div>
        <div className="journal-quote"><label className="field full"><span>Quote for this day</span><input maxLength={500} value={props.quote} onChange={(event) => props.onQuote(event.target.value)} placeholder="Add a quote" /></label><button type="button" className="secondary-button" onClick={props.onSaveQuote}>Save quote</button></div>
        <label className="field full"><span>New note</span><textarea rows={5} maxLength={20000} value={props.text} disabled={busy} onChange={(event) => props.onText(event.target.value)} placeholder="What’s on your mind?" /></label>
        <div className="journal-cleanup"><button type="button" className="secondary-button" disabled={!props.text.trim() || busy} onClick={props.onCleanup}>{props.cleanupStatus === 'Cleaning…' ? 'Cleaning…' : 'Fix text'}</button>{props.cleanupStatus && props.cleanupStatus !== 'Cleaning…' && <span className="cleanup-status">{props.cleanupStatus}</span>}</div>
        <div className="dialog-footer"><span className="journal-status">{props.status}</span><button type="button" className="primary-button" disabled={!props.text.trim() || busy} onClick={props.onAdd}>Save note</button></div>
      </section>
    </div>
  );
}

function SettingsPanel(props: { hour12: boolean; syncStatus: string; onSetHour12: (value: boolean) => void; onSync: () => void; onExport: () => void; onImport: (file: File) => Promise<string>; onClose: () => void }) {
  const [importStatus, setImportStatus] = useState('');
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) props.onClose(); }}>
      <section className="settings-panel">
        <div className="dialog-header"><div><span className="eyebrow">Preferences</span><h2>Calendar settings</h2></div><button className="icon-button" onClick={props.onClose} aria-label="Close">×</button></div>
        <div className="setting-row"><div><strong>Time format</strong><small>Choose how event times are displayed.</small></div><div className="segmented-control"><button className={props.hour12 ? 'selected' : ''} onClick={() => props.onSetHour12(true)}>12 hour</button><button className={!props.hour12 ? 'selected' : ''} onClick={() => props.onSetHour12(false)}>24 hour</button></div></div>
        <div className="setting-row"><div><strong>Cloudflare D1 storage</strong><small>This planner is connected to a public D1 database. Nothing is stored in this browser.</small></div><span className="local-badge"><span className="saved-dot" />{props.syncStatus}</span></div>
        <div className="sync-actions"><button type="button" className="secondary-button" onClick={props.onSync}>Refresh from D1</button></div>
        <p className="sync-note">This database is intentionally public: anyone with the planner URL can read and change calendar or journal data.</p>
        <div className="setting-row"><div><strong>Backup</strong><small>Export or import a JSON file manually when you need to move data.</small></div></div>
        <div className="backup-controls"><button className="secondary-button" onClick={props.onExport}>Export backup</button><label className="secondary-button import-button">Import backup<input type="file" accept="application/json,.json" onChange={async (event) => { const file = event.target.files?.[0]; if (file) setImportStatus(await props.onImport(file)); event.target.value = ''; }} /></label></div>
        {importStatus && <p className="backup-status" role="status">{importStatus}</p>}
      </section>
    </div>
  );
}

export default function App(): JSX.Element {
  const today = useMemo(() => plannerDateForNow(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [focusNow, setFocusNow] = useState(0);
  const [zoneOrder, setZoneOrder] = useState<ZoneId[]>(() => zones.map((zone) => zone.id));
  const [view, setView] = useState<ViewMode>('day');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [now, setNow] = useState(new Date());
  const lastHomeDate = useRef(today);
  const [collapsed, setCollapsed] = useState(false);
  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journalView, setJournalView] = useState(false);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [journalText, setJournalText] = useState('');
  const [journalStatus, setJournalStatus] = useState('');
  const [journalBrowseStatus, setJournalBrowseStatus] = useState('');
  const [journalExportStatus, setJournalExportStatus] = useState('');
  const [journalCleanupStatus, setJournalCleanupStatus] = useState('');
  const journalSaving = useRef(false);
  const journalCleaning = useRef(false);
  const [dailyQuote, setDailyQuote] = useState('');
  const [journalFilterStart, setJournalFilterStart] = useState('');
  const [journalFilterEnd, setJournalFilterEnd] = useState('');
  const journalLoadId = useRef(0);
  const [hour12, setHour12] = useState(true);
  const [syncKey] = useState('public');
  const [syncStatus, setSyncStatus] = useState('Connecting…');
  const syncKeyRef = useRef(syncKey);
  const syncInFlight = useRef(false);

  const syncEvents = useCallback(async (key: string) => {
    if (!key || syncInFlight.current) return;
    syncInFlight.current = true;
    setSyncStatus('Syncing…');
    try {
      const latest = await cloudRequest(key);
      if (!Array.isArray(latest.records)) throw new Error('Cloud returned an invalid response.');
      if (syncKeyRef.current !== key) return;
      const remote = latest.records.filter((record) => record.event && validImportedEvent(record.event)).map((record) => record.event as CalendarEvent);
      setEvents(remote);
      setSyncStatus('Synced');
    } catch (error) {
      setSyncStatus(error instanceof Error ? error.message : 'Cloud sync is unavailable.');
    } finally {
      syncInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const homeDate = plannerDateForNow(now);
    const previousHomeDate = lastHomeDate.current;
    if (homeDate === previousHomeDate) return;
    lastHomeDate.current = homeDate;
    if ((view === 'day' || view === 'fourDays') && !journalView && selectedDate === previousHomeDate) {
      setSelectedDate(homeDate);
      setFocusNow((value) => value + 1);
    }
  }, [now, view, journalView, selectedDate]);

  useEffect(() => {
    syncKeyRef.current = syncKey;
    if (syncKey) {
      void syncEvents(syncKey);
    } else {
      setEvents([]);
    }
  }, [syncKey, syncEvents]);

  useEffect(() => {
    if (!syncKey) return;
    const timer = window.setInterval(() => { void syncEvents(syncKey); }, 30000);
    return () => window.clearInterval(timer);
  }, [syncKey, syncEvents]);

  const loadJournal = useCallback(async (startDate: string, endDate: string, key = syncKey) => {
    const requestId = ++journalLoadId.current;
    if (startDate && endDate && startDate > endDate) {
      setJournalEntries([]);
      setJournalBrowseStatus('Choose a valid date range');
      return;
    }
    if (!key) {
      setJournalEntries([]);
      setJournalBrowseStatus('Journal unavailable');
      return;
    }
    try {
      setJournalBrowseStatus('Loading…');
      const result = await journalRequest(key, { startDate, endDate });
      if (syncKeyRef.current === key && requestId === journalLoadId.current) {
        setJournalEntries(Array.isArray(result.entries) ? result.entries : []);
        setJournalBrowseStatus('');
      }
    } catch (error) {
      if (requestId === journalLoadId.current) setJournalBrowseStatus(error instanceof Error ? error.message : 'Journal unavailable.');
    }
  }, [syncKey]);

  const loadQuote = useCallback(async (date: string, key = syncKey) => {
    if (!key) return setDailyQuote('');
    try {
      const result = await quoteRequest(key, date);
      if (syncKeyRef.current === key) setDailyQuote(result.quote?.quote || '');
    } catch {
      setDailyQuote('');
    }
  }, [syncKey]);

  useEffect(() => {
    if (journalView) void loadJournal(journalFilterStart, journalFilterEnd);
  }, [journalView, journalFilterStart, journalFilterEnd, loadJournal]);

  useEffect(() => { void loadQuote(selectedDate); }, [selectedDate, loadQuote]);

  const activeZone = getZone(zoneOrder[zoneOrder.length - 1]);
  const makeDraft = (zone: Zone = activeZone, startInstant: Date = shiftStart(selectedDate), endInstant: Date = new Date(startInstant.getTime() + 60 * 60000)): EventDraft => {
    return { title: '', date: dateKey(startInstant, zone.iana), endDate: dateKey(endInstant, zone.iana), start: inputTime(startInstant, zone.iana), end: inputTime(endInstant, zone.iana), zoneId: zone.id, color: zone.color, description: '', kind: 'event', completed: false, work: '' };
  };
  const makeShiftDraft = (zone: Zone, date: string, start: number, end: number) => {
    const base = shiftStart(date).getTime();
    return makeDraft(zone, new Date(base + start * 60000), new Date(base + end * 60000));
  };
  const openEvent = (event: CalendarEvent) => {
    const zone = activeZone;
    const start = new Date(event.startUtc);
    const end = new Date(event.endUtc);
    setDraft({ id: event.id, title: event.title, date: dateKey(start, zone.iana), endDate: dateKey(end, zone.iana), start: inputTime(start, zone.iana), end: inputTime(end, zone.iana), zoneId: zone.id, color: event.color, description: event.description, kind: event.kind || 'event', completed: !!event.completed, work: event.work || '' });
  };
  const saveEvent = async () => {
    if (!draft || !draft.title.trim()) return;
    const instants = draftInstants(draft);
    if (!instants || instants.end <= instants.start) return;
    if (!syncKey) {
      setSyncStatus('D1 is unavailable');
      setSettingsOpen(true);
      return;
    }
    const updated: CalendarEvent = { id: draft.id || makeId(), title: draft.title.trim(), startUtc: instants.start.toISOString(), endUtc: instants.end.toISOString(), zoneId: draft.zoneId, color: draft.color, description: draft.description.trim(), kind: draft.kind, completed: draft.kind === 'task' && draft.completed, work: draft.work.trim() };
    try {
      setSyncStatus('Saving…');
      await cloudRequest(syncKey, [{ id: updated.id, event: updated }]);
      setDraft(null);
      await syncEvents(syncKey);
    } catch (error) {
      setSyncStatus(error instanceof Error ? error.message : 'Could not save to D1.');
    }
  };
  const deleteEvent = async () => {
    if (!draft || !draft.id) return;
    if (!syncKey) {
      setSyncStatus('D1 is unavailable');
      setSettingsOpen(true);
      return;
    }
    try {
      setSyncStatus('Deleting…');
      await cloudRequest(syncKey, [{ id: draft.id, event: null }]);
      setDraft(null);
      await syncEvents(syncKey);
    } catch (error) {
      setSyncStatus(error instanceof Error ? error.message : 'Could not delete from D1.');
    }
  };
  const persistTimelineEvent = async (updated: CalendarEvent) => {
    setEvents((current) => current.map((item) => item.id === updated.id ? updated : item));
    try {
      setSyncStatus('Saving…');
      await cloudRequest(syncKey, [{ id: updated.id, event: updated }]);
      setSyncStatus('Synced');
    } catch (error) {
      setSyncStatus(error instanceof Error ? error.message : 'Could not save to D1.');
      void syncEvents(syncKey);
    }
  };
  const resizeEvent = async (event: CalendarEvent, endUtc: string) => {
    if (new Date(endUtc).getTime() <= new Date(event.startUtc).getTime()) return;
    await persistTimelineEvent({ ...event, endUtc });
  };
  const moveEvent = async (event: CalendarEvent, startUtc: string) => {
    if (startUtc === event.startUtc) return;
    await persistTimelineEvent(relocateEvent(event, startUtc));
  };
  const toggleTask = async (task: CalendarEvent) => {
    const updated = { ...task, completed: true };
    setEvents((current) => current.filter((item) => item.id !== task.id));
    try {
      await cloudRequest(syncKey, [{ id: updated.id, event: updated }]);
    } catch (error) {
      setSyncStatus(error instanceof Error ? error.message : 'Could not complete task.');
      void syncEvents(syncKey);
    }
  };
  const moveDate = (amount: number) => {
    if (journalView) {
      const date = addDays(selectedDate, amount);
      setSelectedDate(date);
      setJournalFilterStart(date);
      setJournalFilterEnd(date);
      setJournalExportStatus('');
      return;
    }
    if (view === 'month') return moveMonth(amount);
    const step = view === 'week' ? 7 : view === 'fourDays' ? 4 : 1;
    setSelectedDate(addDays(selectedDate, amount * step));
  };
  const moveMonth = (amount: number) => {
    const date = new Date(selectedDate + 'T12:00:00Z');
    date.setUTCMonth(date.getUTCMonth() + amount);
    setSelectedDate(date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-01');
  };
  const reorderZones = (from: ZoneId, to: ZoneId) => {
    if (from === to) return;
    setZoneOrder((current) => {
      const movingForward = current.indexOf(from) < current.indexOf(to);
      const next = current.filter((id) => id !== from);
      const targetIndex = next.indexOf(to);
      next.splice(targetIndex < 0 ? next.length : targetIndex + (movingForward ? 1 : 0), 0, from);
      return next;
    });
  };
  const exportBackup = () => {
    const backup = { version: 1, exportedAt: new Date().toISOString(), events };
    const url = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'three-zone-calendar-' + dateKey(new Date(), getZone('philippines').iana) + '.json';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const importBackup = async (file: File): Promise<string> => {
    try {
      const parsed = JSON.parse(await file.text()) as { version?: number; events?: unknown };
      if (parsed.version !== 1 || !Array.isArray(parsed.events) || !parsed.events.every(validImportedEvent)) return 'This file is not a valid calendar backup. Nothing was imported.';
      const imported = parsed.events as CalendarEvent[];
      await cloudRequest(syncKey, imported.map((event) => ({ id: event.id, event })));
      await syncEvents(syncKey);
      return 'Imported ' + imported.length + ' events into D1. Events with matching IDs were updated.';
    } catch {
      return 'Could not read this backup file. Nothing was imported.';
    }
  };
  const addJournalEntry = async (content = journalText, fromCleanup = false) => {
    if (!syncKey || !content.trim() || journalSaving.current || (journalCleaning.current && !fromCleanup)) return;
    journalSaving.current = true;
    try {
      setJournalStatus('Saving…');
      await journalRequest(syncKey, { date: selectedDate, content });
      setJournalText('');
      setJournalStatus('');
      setJournalCleanupStatus('');
      setJournalOpen(false);
      if (journalView) void loadJournal(journalFilterStart, journalFilterEnd, syncKey);
    } catch (error) {
      setJournalText(content);
      setJournalCleanupStatus('');
      setJournalStatus(error instanceof Error ? error.message : 'Could not save journal note.');
    } finally {
      journalSaving.current = false;
    }
  };
  const cleanupJournal = async () => {
    if (!journalText.trim() || journalSaving.current || journalCleaning.current) return;
    journalCleaning.current = true;
    try {
      setJournalCleanupStatus('Cleaning…');
      const result = await cleanupJournalRequest(journalText.trim());
      if (!result.cleaned?.trim()) throw new Error('No cleaned text was returned. Your note was not saved.');
      await addJournalEntry(result.cleaned, true);
    } catch (error) {
      setJournalCleanupStatus(error instanceof Error ? error.message : 'Could not clean up journal note.');
    } finally {
      journalCleaning.current = false;
    }
  };
  const saveQuote = async () => {
    try {
      await quoteRequest(syncKey, selectedDate, dailyQuote);
      setJournalStatus('Quote saved');
    } catch (error) {
      setJournalStatus(error instanceof Error ? error.message : 'Could not save quote.');
    }
  };
  const exportJournalCsv = async () => {
    if (journalFilterStart && journalFilterEnd && journalFilterStart > journalFilterEnd) {
      setJournalExportStatus('Choose a valid date range');
      return;
    }
    try {
      setJournalExportStatus('Preparing CSV…');
      const result = await journalRequest(syncKey, { startDate: journalFilterStart, endDate: journalFilterEnd });
      const entries = Array.isArray(result.entries) ? result.entries : [];
      const csvCell = (value: string) => '"' + value.replace(/"/g, '""') + '"';
      const csv = [['From', journalFilterStart || 'All dates', 'To', journalFilterEnd || 'All dates'], ['Date', 'Created at', 'Entry'], ...entries.map((entry) => [entry.journal_date, new Date(entry.created_at).toISOString(), entry.content])]
        .map((row) => row.map(csvCell).join(',')).join('\r\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'three-zone-journal-' + (journalFilterStart || 'all') + '-to-' + (journalFilterEnd || 'all') + '.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setJournalExportStatus('Exported ' + entries.length + ' entries');
    } catch (error) {
      setJournalExportStatus(error instanceof Error ? error.message : 'Could not export journal.');
    }
  };
  const rangeStart = view === 'week' ? startOfWeekMonday(selectedDate) : view === 'fourDays' ? fourDayStartDate(selectedDate, now) : selectedDate;
  const rangeEnd = addDays(rangeStart, view === 'week' ? 6 : 3);
  const headerTitle = journalView ? 'Journal'
    : view === 'month'
    ? monthTitle(selectedDate)
    : view === 'day'
      ? dateTitle(selectedDate, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      : dateTitle(rangeStart, { month: 'short', day: 'numeric' }) + ' – ' + dateTitle(rangeEnd, { month: 'short', day: 'numeric', year: 'numeric' });
  const compactHeaderTitle = journalView ? 'Journal'
    : view === 'month'
    ? dateTitle(selectedDate, { month: 'short', year: 'numeric' })
    : view === 'day'
      ? dateTitle(selectedDate, { month: 'short', day: 'numeric' })
      : dateTitle(rangeStart, { month: 'short', day: 'numeric' }) + '–' + dateTitle(rangeEnd, { day: 'numeric' });

  return (
    <div className={'app-shell' + (journalView ? ' journal-mode' : '')}>
      <header className="topbar">
        <div className="brand-area"><button className="icon-button menu-button" onClick={() => setCollapsed(!collapsed)} aria-label="Toggle sidebar">☰</button><div className="brand-mark"><span>3</span></div><span className="brand-name">Daphne&apos;s <strong>Calendar</strong></span></div>
        <div className="topbar-controls"><button className="today-button" onClick={() => { const date = plannerDateForNow(new Date()); setSelectedDate(date); if (journalView) { setJournalFilterStart(date); setJournalFilterEnd(date); setJournalExportStatus(''); } else setFocusNow((value) => value + 1); }}>Today</button><div className="nav-buttons"><button className="icon-button" onClick={() => moveDate(-1)} aria-label="Previous date">‹</button><button className="icon-button" onClick={() => moveDate(1)} aria-label="Next date">›</button></div><h1 aria-label={headerTitle}><span className="wide-date-title">{headerTitle}</span><span className="compact-date-title" aria-hidden="true">{compactHeaderTitle}</span></h1></div>
        <div className="topbar-actions"><button className="storage-status" onClick={() => setSettingsOpen(true)} title={syncKey ? 'Cloud storage: ' + syncStatus : 'Cloud storage unavailable'} aria-label={syncKey ? 'Cloud storage connected' : 'Cloud storage unavailable'}><span className="saved-dot" /></button><button className={'journal-button' + (journalView ? ' active' : '')} onClick={() => setJournalView((current) => !current)}>{journalView ? 'Calendar' : 'Journal'}</button><label className="view-select"><span className="sr-only">Calendar view</span><select value={view} onChange={(event) => { setView(event.target.value as ViewMode); setJournalView(false); }}><option value="day">DAY</option><option value="week">WEEK</option><option value="fourDays">4 DAY</option><option value="month">MONTH</option></select></label><button className="icon-button" onClick={() => setSettingsOpen(true)} aria-label="Settings">⚙</button><div className="avatar">D</div></div>
      </header>
      <div className="workspace">
        <Sidebar selectedDate={selectedDate} collapsed={collapsed} onSelect={(date) => { setSelectedDate(date); if (journalView) { setJournalFilterStart(date); setJournalFilterEnd(date); setJournalExportStatus(''); } }} onMoveMonth={moveMonth} onExpand={() => setCollapsed(false)} quote={dailyQuote} tasks={events.filter((event) => event.kind === 'task' && !event.completed)} onToggleTask={(task) => void toggleTask(task)} />
        <main className="main-content">
          {journalView ? <JournalView date={selectedDate} entries={journalEntries} status={journalBrowseStatus} exportStatus={journalExportStatus} start={journalFilterStart} end={journalFilterEnd} onStart={(value) => { setJournalFilterStart(value); setJournalExportStatus(''); }} onEnd={(value) => { setJournalFilterEnd(value); setJournalExportStatus(''); }} onClear={() => { setJournalFilterStart(''); setJournalFilterEnd(''); setJournalExportStatus(''); }} onWrite={(date) => { setSelectedDate(date); setJournalStatus(''); setJournalOpen(true); }} onExport={() => void exportJournalCsv()} /> : view === 'day' ? <TimeSheet zoneOrder={zoneOrder} selectedDate={selectedDate} events={events} now={now} hour12={hour12} focusNow={focusNow} onReorder={reorderZones} onCreate={(zone, date, start, end) => setDraft(makeShiftDraft(zone, date, start, end))} onOpen={openEvent} onResize={resizeEvent} onMove={moveEvent} /> : view === 'month' ? <MonthView selectedDate={selectedDate} primaryZone={activeZone} events={events} hour12={hour12} onCreate={(date) => setDraft(makeDraft(activeZone, wallTimeToUtc(date + 'T09:00', activeZone.iana)))} onOpen={openEvent} /> : <MultiDayView dayCount={view === 'fourDays' ? 4 : 7} zoneOrder={zoneOrder} selectedDate={selectedDate} events={events} now={now} hour12={hour12} focusNow={focusNow} onReorder={reorderZones} onCreate={(zone, date, start, end) => setDraft(makeShiftDraft(zone, date, start, end))} onOpen={openEvent} onResize={resizeEvent} onMove={moveEvent} />}
          {!journalView && <CreateMenu compact onCreate={(kind) => setDraft({ ...makeDraft(), kind })} />}
        </main>
      </div>
      {draft && (draft.kind === 'task' ? <QuickTaskDialog draft={draft} onChange={setDraft} onClose={() => setDraft(null)} onSave={saveEvent} onDelete={deleteEvent} /> : !draft.id ? <QuickEventDialog draft={draft} hour12={hour12} onChange={setDraft} onClose={() => setDraft(null)} onSave={saveEvent} /> : <EventDialog draft={draft} hour12={hour12} onChange={setDraft} onClose={() => setDraft(null)} onSave={saveEvent} onDelete={deleteEvent} />)}
      {journalOpen && <JournalPanel date={selectedDate} status={journalStatus} text={journalText} quote={dailyQuote} cleanupStatus={journalCleanupStatus} onText={(value) => { setJournalText(value); setJournalCleanupStatus(''); }} onQuote={setDailyQuote} onSaveQuote={() => void saveQuote()} onCleanup={() => void cleanupJournal()} onAdd={() => void addJournalEntry()} onClose={() => setJournalOpen(false)} />}
      {settingsOpen && <SettingsPanel hour12={hour12} syncStatus={syncStatus} onSetHour12={setHour12} onSync={() => void syncEvents(syncKey)} onExport={exportBackup} onImport={importBackup} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
